<template>
    <nav 
        aria-label="Breadcrumb" 
        class="breadcrumb-nav"
        v-if="breadcrumbs.length > 0">
        <ol class="breadcrumb-list">
            <li 
                v-for="(crumb, index) in breadcrumbs" 
                :key="index"
                class="breadcrumb-item">
                <v-icon 
                    v-if="index > 0" 
                    small 
                    class="breadcrumb-separator"
                    aria-hidden="true">
                    mdi-chevron-right
                </v-icon>
                <router-link 
                    v-if="index < breadcrumbs.length - 1"
                    :to="crumb.path"
                    class="breadcrumb-link"
                    :aria-label="$t('accessibility.navigateTo', { page: crumb.label })">
                    {{ formatMenuText(crumb.label) }}
                </router-link>
                <span 
                    v-else 
                    class="breadcrumb-current"
                    aria-current="page">
                    {{ formatMenuText(crumb.label) }}
                </span>
            </li>
        </ol>
    </nav>
</template>

<script>
export default {
    name: 'Breadcrumb',
    computed: {
        breadcrumbs() {
            const route = this.$route;
            const breadcrumbs = [];

            // Always include home
            breadcrumbs.push({
                label: this.$t('home'),
                path: '/'
            });

            // Get breadcrumb data from route meta
            if (route.meta && route.meta.breadcrumb) {
                if (Array.isArray(route.meta.breadcrumb)) {
                    // Multiple breadcrumbs from route
                    breadcrumbs.push(...route.meta.breadcrumb.map(crumb => ({
                        label: this.stripHtml(typeof crumb.label === 'function' ? crumb.label(route) : this.$t(crumb.label)),
                        path: crumb.path
                    })));
                } else {
                    // Single breadcrumb from route
                    breadcrumbs.push({
                        label: this.stripHtml(this.$t(route.meta.breadcrumb)),
                        path: route.path
                    });
                }
            } else if (route.name) {
                // Fallback to route name
                const label = this.getRouteLabel(route.name);
                if (label) {
                    breadcrumbs.push({
                        label: this.stripHtml(label),
                        path: route.path
                    });
                }
            }

            return breadcrumbs;
        }
    },
    methods: {
        formatMenuText(text) {
            if (!text) return text;

            // Check the titleCase setting from the current experience
            const titleCase = this.$experience.titleCase !== false; // default to true if not set

            if (titleCase) {
                // Title case: capitalize first letter of all words
                return text.split(' ').map(word => {
                    if (word.length === 0) return word;
                    // Check if word is an acronym (all uppercase)
                    if (word === word.toUpperCase() && word.length > 1) {
                        return word; // Keep acronyms as-is
                    }
                    return word.charAt(0).toUpperCase() + word.slice(1);
                }).join(' ');
            } else {
                // Sentence case: only capitalize first letter of first word
                // But preserve acronyms
                const words = text.split(' ');
                return words.map((word, index) => {
                    if (word.length === 0) return word;
                    // Check if word is an acronym (all uppercase)
                    if (word === word.toUpperCase() && word.length > 1) {
                        return word; // Keep acronyms as-is
                    }
                    if (index === 0) {
                        // First word: capitalize first letter
                        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                    } else {
                        // Other words: all lowercase
                        return word.toLowerCase();
                    }
                }).join(' ');
            }
        },
        getRouteLabel(routeName) {
            // Map route names to translation keys
            const routeLabels = {
                'home': this.$t('home'),
                'dashboard': this.$t('dashboard'),
                'login': this.$t('login-view.login'),
                'register': this.$t('login-view.register'),
                'forgotPassword': this.$t('login-view.forgotPassword'),
                'resetPassword': this.$t('reset-password.title'),
                'mfaCodeValidation': this.$t('login-view.mfaTitle'),
                'userManagement': this.$t('user-management.title'),
                'reports': this.$t('reports.menu'),
                'faq': this.$t('app.faq')
            };

            return routeLabels[routeName] || routeName;
        },
        stripHtml(html) {
            // Strip HTML tags for accessibility labels
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || '';
        }
    },
    watch: {
        '$route'() {
            // Announce navigation to screen readers
            if (this.breadcrumbs.length > 0) {
                const currentPage = this.breadcrumbs[this.breadcrumbs.length - 1].label;
                this.$announcer.announceNavigation(currentPage);
            }
        }
    }
};
</script>

<style scoped>
.breadcrumb-nav {
    padding: 4px 0;
    margin-bottom: 0;
}

.breadcrumb-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.875rem;
}

.breadcrumb-item {
    display: flex;
    align-items: center;
}

.breadcrumb-separator {
    margin: 0 4px;
    color: var(--v-textColor-base, #4A5C62);
    opacity: 0.6;
}

.breadcrumb-link {
    color: var(--v-primary-base, #006891);
    text-decoration: none;
    transition: color 0.2s ease;
}

.breadcrumb-link:hover {
    color: var(--v-linkHover-base, #C10073);
    text-decoration: underline;
}

.breadcrumb-link:focus {
    outline: 3px solid var(--v-focusOutlineColor-base, #0066CC);
    outline-offset: 2px;
    border-radius: 2px;
}

.breadcrumb-current {
    color: var(--v-textColor-base, #4A5C62);
    font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 600px) {
    .breadcrumb-list {
        font-size: 0.8125rem;
    }

    .breadcrumb-separator {
        margin: 0 4px;
    }
}
</style>
